import React from 'react';
import { motion } from 'framer-motion';
export function Section5() {
  return <motion.section initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6
  }} viewport={{
    once: true
  }} className="max-w-3xl mx-auto px-6 py-16" data-id="element-101">
      <h2 className="font-sans font-bold text-3xl text-[#0F4226] mb-8" data-id="element-102">
        5. The Science — Why Plant Chemistry Actually Works
      </h2>

      <div className="mb-8" data-id="element-103">
        <img src="https://cdn.magicpatterns.com/uploads/s1ZXyFQLShzhPejs5rZx3e/Dog_resting_in_202604192244_(4).jpg" alt="Tick on a leaf surrounded by plant-derived molecular compounds" className="w-full rounded shadow-md" data-id="element-104" />
      </div>

      <div className="font-sans text-lg leading-relaxed text-[#666666] space-y-6" data-id="element-105">
        <p data-id="element-106">
          This is where ancient wisdom meets modern research. Plant-derived
          compounds genuinely and scientifically work to prevent fleas and ticks
          from finding your pet. This is not pseudoscience. This is now
          confirmed biology.
        </p>

        <div className="bg-[#4DC61A]/10 border-l-4 border-[#4DC61A] p-6 my-8 rounded" data-id="element-107">
          <h3 className="font-sans font-bold text-xl mb-4 text-[#0F4226]" data-id="element-108">
            The Parasite Detection Window
          </h3>
          <p data-id="element-109">
            Fleas and ticks don't find hosts randomly. They are sophisticated
            sensory hunters that detect body heat, exhaled CO2, specific skin
            chemicals, and movement from distances of up to 18 inches. They
            locate the host <em data-id="element-110">before</em> they ever make contact. This is the
            moment — the detection window — that no existing product has ever
            addressed.
          </p>
        </div>

        <h3 className="font-sans font-bold text-2xl mt-8 mb-4 text-[#0F4226]" data-id="element-111">
          How Plant VOCs Work
        </h3>
        <p data-id="element-112">
          Plant-derived volatile organic compounds (VOCs) — the same compounds
          that plants have evolved over millions of years to defend themselves
          against insects — interfere with the specific olfactory and chemical
          sensory receptors that fleas and ticks use to detect hosts.
        </p>

        <div className="bg-[#4DC61A]/10 border-l-4 border-[#4DC61A] p-6 my-8 rounded" data-id="element-113">
          <h3 className="font-sans font-bold text-xl mb-4 text-[#0F4226]" data-id="element-114">
            The Octopamine Receptor Mechanism
          </h3>
          <p data-id="element-115">
            These compounds disrupt octopamine receptors — a neurological
            pathway present in invertebrates but{' '}
            <strong className="text-[#0F4226]" data-id="element-116">NOT in mammals</strong>. This is
            the critical safety distinction. It's effective against parasites
            precisely because their sensory biology is fundamentally different
            from ours and our pets'.
          </p>
        </div>

        <h3 className="font-sans font-bold text-2xl mt-8 mb-4 text-[#0F4226]" data-id="element-117">
          The Peer-Reviewed Evidence
        </h3>
        <ul className="list-disc pl-6 space-y-3" data-id="element-118">
          <li data-id="element-119">
            <strong className="text-[#0F4226]" data-id="element-120">Cedarwood oil components</strong>{' '}
            (cedrol, thujopsene) — repellent activity against lone star tick
            documented in <em data-id="element-121">Journal of Agricultural and Food Chemistry</em>
          </li>
          <li data-id="element-122">
            <strong className="text-[#0F4226]" data-id="element-123">Peppermint oil</strong> —
            repellent effects against deer tick in{' '}
            <em data-id="element-124">Parasitology Research</em>
          </li>
          <li data-id="element-125">
            <strong className="text-[#0F4226]" data-id="element-126">Geraniol</strong> — up to 90%
            reduction in tick attachment in <em data-id="element-127">Journal of Vector Ecology</em>
          </li>
          <li data-id="element-128">
            <strong className="text-[#0F4226]" data-id="element-129">Neem compounds</strong> —
            significant reduction in tick attachment rates in dogs per{' '}
            <em data-id="element-130">Veterinary Parasitology</em>
          </li>
        </ul>

        <p className="text-xl font-medium italic border-l-4 border-[#4DC61A] pl-6 py-4 my-8 bg-[#4DC61A]/10 text-[#0F4226]" data-id="element-131">
          The plants that ancient Egyptians hung in their doorways and Roman
          farmers tucked into their livestock bedding were disrupting tick
          sensory receptors. They just didn't know to call it that yet.
        </p>

        <p className="text-lg font-semibold text-[#0F4226]" data-id="element-132">
          The result for your pet: From the parasite's perspective, a pet
          protected by plant VOCs simply doesn't exist. The biological signals
          that would normally attract them are masked and scrambled. They cannot
          find what they cannot detect.
        </p>
      </div>
    </motion.section>;
}